/* ────────────────────────────────────────────────────────────────
   StepBillUpload.jsx
   Sube el recibo PDF, obtiene JSON, calcula la cotización
────────────────────────────────────────────────────────────────── */
import React, { useState, useCallback } from "react";
import { Box, Button, Typography, Stack, Backdrop, Alert } from "@mui/material";
import { useDropzone } from "react-dropzone";

import ScanAnimation from "./ScanAnimation";

import { extractCfeTable } from "../../api/extract";
import { quoteFromTable } from "../../utils/quoteFromTable";
import { detectPeriodicity } from "../../utils/detectPeriodicity";

/* utilidad */
const isPdf = (f) => /\.pdf$/i.test(f.name);

export default function StepBillUpload({ onComplete, onBack }) {
  const [file, setFile] = useState(null); // solo PDF
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* drag-and-drop */
  const onDrop = useCallback((accepted) => {
    if (!accepted.length) return;
    const f = accepted[0];

    if (!isPdf(f)) {
      setError("Solo se permite subir el recibo en formato PDF.");
      return;
    }

    setFile(f);
    setError("");
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { "application/pdf": [] },
  });

  /* procesa el PDF → cotización */
  const handleProcess = async () => {
    if (!file) return;
    setLoading(true);
    setError("");

    try {
      /* 1. PDF → tabla JSON vía FastAPI */
      const table = await extractCfeTable(file);

      /* 2. mensual o bimestral */
      const tipo = detectPeriodicity(table);

      /* 3. cotiza (sin override) */
      const quote = quoteFromTable(table, tipo);

      /* 4. siguiente paso */
      onComplete([file], quote);
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al procesar el PDF");
    } finally {
      setLoading(false);
    }
  };

  /* loading overlay */
  if (loading) {
    return (
      <Backdrop open sx={{ zIndex: 1300, bgcolor: "#fff" }}>
        <ScanAnimation size={450} />
        <Typography color="text.secondary" mt={2}>
          Analizando tu recibo…
        </Typography>
      </Backdrop>
    );
  }

  /* UI */
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* zona drag-and-drop */}
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed #e06a00",
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          textAlign: "center",
          bgcolor: isDragActive ? "rgba(224,106,0,.08)" : "rgba(224,106,0,.04)",
          cursor: "pointer",
          transition: ".25s",
        }}
      >
        <input {...getInputProps()} />
        {file ? (
          <Typography fontWeight={600}>{file.name}</Typography>
        ) : (
          <>
            <Typography variant="subtitle1" fontWeight={700}>
              Arrastra tu recibo PDF
            </Typography>
            <Typography variant="body2" color="text.secondary">
              (máx 1 archivo)
            </Typography>
          </>
        )}
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" justifyContent="space-between">
        <Button onClick={onBack}>Atrás</Button>
        <Button
          variant="contained"
          disabled={!file}
          onClick={handleProcess}
          sx={{ textTransform: "none" }}
        >
          Siguiente
        </Button>
      </Stack>
    </Box>
  );
}
